import { useParams } from '@tanstack/react-router';
import { ChevronDown, FileText, History, Network, Plus, Settings, Settings2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { type CitationFocus, DocumentViewerContent } from '@/components/case-partner/DocumentViewerContent';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { type SidePanelTab, useSidePanel } from '@/components/contexts/SidePanelContext';
import { DeepAnalysisDocumentDialog } from '@/components/deep-analysis/DeepAnalysisDocumentDialog';
import { DeepAnalysisHeaderControl } from '@/components/deep-analysis/DeepAnalysisHeaderControl';
import { AuditPanel } from '@/components/knowledge-graph/AuditPanel';
import { ChronologyDialog } from '@/components/knowledge-graph/ChronologyDialog';
import { ChronologyPanel } from '@/components/knowledge-graph/ChronologyPanel';
import { DetailNavigator } from '@/components/knowledge-graph/DetailPanel';
import { EventEditDialog } from '@/components/knowledge-graph/EventEditDialog';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Button } from '@/components/ui/NewButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CHRONOLOGIES_PER_MATTER_CAP,
  type ChronologyInfo,
  SYSTEM_CHRONOLOGY_TYPES,
  useChronologies,
} from '@/hooks/useChronologies';
import {
  type ChronologyEvent,
  type ChronologyEventLight,
  useChronology,
} from '@/hooks/useChronology';
import { useEventEditing } from '@/hooks/useChronologyEditing';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { useEntities } from '@/hooks/useEntities';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { usePipelineProgress } from '@/hooks/usePipelineProgress';
import { apiErrorMessage } from '@/lib/api-client';

const DOCUMENT_TAB_ID = 'document';
const EVENT_DETAILS_TAB_ID = 'event-details';
const ENTITY_CASE_THEORY_TAB_ID = 'case-theory';
const Chronology = () => {
  const { selectedCase } = useCaseContext();
  const { slug } = useParams({ from: '/matter/$slug/chronology' });
  const { isOpen, openPanel, closePanel, tabs, setTabs, setActiveTab } = useSidePanel();

  const matterGuid = slug;
  const {
    isEnabled: deepAnalysisEnabled,
    isLoading: deepAnalysisAvailabilityLoading,
  } = useDeepAnalysisAvailability(matterGuid);
  const { isEnabled: customChronologiesEnabled } = useFeatures(FEATURE_FLAGS.CUSTOM_CHRONOLOGIES);
  const deepAnalysisMatterGuid = deepAnalysisEnabled ? matterGuid : undefined;
  const { chronologies, deleteChronology, isLoading: chronologiesLoading } = useChronologies(deepAnalysisMatterGuid);
  const { softDeleteEvent } = useEventEditing(matterGuid);
  const { entities } = useEntities(deepAnalysisMatterGuid);
  const {
    progress,
    isLoading: pipelineProgressLoading,
    mutate: mutatePipelineProgress,
  } = usePipelineProgress(deepAnalysisMatterGuid);

  const [activeType, setActiveType] = useState<string | null | undefined>(undefined);
  // Dialog mode is encoded in `dialogTarget`: null = closed,
  // 'create' = blank create form, otherwise = chronology id to edit.
  const [dialogTarget, setDialogTarget] = useState<'create' | string | null>(null);
  // Event sheet state. ``open`` toggles visibility independently from the
  // event payload so the closing animation doesn't flash empty content.
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ChronologyEvent | null>(null);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  // Search lives at the page level so it survives switching chronology tabs,
  // which remounts ChronologyPanel and resets its other filters.
  const [searchQuery, setSearchQuery] = useState('');

  const activeChronology = useMemo(
    () => activeType && activeType !== 'all'
      ? chronologies.find(c => c.chronology_type === activeType) ?? null
      : null,
    [activeType, chronologies],
  );

  // The active chronology's description is rendered inside the panel content
  // (see ChronologyPanel) rather than the shell subheader: its length varies
  // per tab (and can be empty), which made the header jump/flicker on switch.
  const chronologyDescription = activeChronology
    ? activeChronology.description || undefined
    : 'Combined view of events across all chronologies.';

  const headerActions = useMemo(() => matterGuid && deepAnalysisEnabled ? (
    <DeepAnalysisHeaderControl
      currentDocuments={progress?.scope?.current}
      totalDocuments={progress?.scope?.total}
      removingDocuments={progress?.scope?.removing}
      isLoading={pipelineProgressLoading}
      onClick={() => setAnalysisDialogOpen(true)}
    />
  ) : null, [deepAnalysisEnabled, matterGuid, pipelineProgressLoading, progress]);

  useShellHeader({
    header: 'Chronology',
    headerActions,
  });

  const chronologyRefreshStatus = useMemo(() => {
    if (!progress) return null;
    if (!activeChronology) return null;
    const totalDocuments = progress.stages.chronology_consolidated.total;
    if (totalDocuments === 0) return null;

    const relevantProjection = progress.projections.find(
      projection => projection.chronology_id === activeChronology.id,
    );
    if (!relevantProjection) return null;

    if (relevantProjection.drafted_periods != null) {
      return {
        label: 'Drafting chronology',
        tooltip: 'Entries appear as they are written. They are read-only, and may still be merged or tidied, until the chronology is published.',
      };
    }

    if (relevantProjection.synced_to_latest_trunk) return null;

    if (relevantProjection.waiting_for_documents) {
      return {
        label: 'Update pending',
        tooltip: 'This chronology will refresh once an update to this matter\'s documents finishes. It stays readable until then.',
        idle: true,
      };
    }

    return {
      label: 'Updating chronology',
      tooltip: 'This chronology is still catching up with the latest evidence.',
    };
  }, [activeChronology, progress]);

  const chronologyProgressSignature = useMemo(() => {
    if (!progress) return null;
    const relevantProjections = activeChronology
      ? progress.projections.filter(
        projection => projection.chronology_id === activeChronology.id,
      )
      : progress.projections;
    if (relevantProjections.length === 0) return null;

    return JSON.stringify({
      chronologyId: activeChronology?.id ?? 'all',
      total: progress.stages.chronology_consolidated.total,
      projections: relevantProjections.map(projection => ({
        chronology_id: projection.chronology_id,
        synced_to_latest_trunk: projection.synced_to_latest_trunk,
        remaining_cell_dates: projection.remaining_cell_dates ?? 0,
        remaining_cell_revision: projection.remaining_cell_revision ?? '',
        projected_documents: projection.projected_documents,
        available_documents: projection.available_documents,
        drafted_periods: projection.drafted_periods ?? null,
      })),
    });
  }, [activeChronology, progress]);

  const previousChronologyProgressSignatureRef = useRef<string | null>(null);

  const eventScopeReady = activeType !== undefined;

  // Fetch events for the selected chronology id (null = all chronologies).
  const { events, isLoading, isError, mutate } = useChronology(
    eventScopeReady && deepAnalysisEnabled ? matterGuid : undefined,
    activeChronology?.id ?? undefined,
  );

  useEffect(() => {
    if (!eventScopeReady || !chronologyProgressSignature) return;

    const previousSignature = previousChronologyProgressSignatureRef.current;
    previousChronologyProgressSignatureRef.current = chronologyProgressSignature;
    if (previousSignature && previousSignature !== chronologyProgressSignature) {
      void mutate();
    }
  }, [eventScopeReady, chronologyProgressSignature, mutate]);

  const handleAnalysisDocumentsChanged = useCallback(async () => {
    await Promise.all([
      mutatePipelineProgress(),
      mutate(),
    ]);
  }, [mutate, mutatePipelineProgress]);

  // Resolve chronology_id from the event's chronology_type (used by the
  // edit panel for matter-scoped lookups: tags, config, etc.
  const resolveChronologyId = useCallback((ev: ChronologyEventLight): string | null => {
    if (ev.chronology_id) return ev.chronology_id;
    if (!ev.chronology_type) return null;
    const c = chronologies.find(c => c.chronology_type === ev.chronology_type);
    return c?.id || null;
  }, [chronologies]);

  // Build chronology type tabs from backend
  const chronologyTabs = useMemo(() => {
    const items = chronologies.map(c => ({ key: c.chronology_type, label: c.name }));
    return [{ key: 'all', label: 'All' }, ...items];
  }, [chronologies]);

  // Auto-select the first chronology once the list arrives
  const autoSelectedRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection whenever the matter changes.
  useEffect(() => {
    autoSelectedRef.current = false;
    setActiveType(undefined);
  }, [matterGuid, deepAnalysisEnabled]);

  useEffect(() => {
    if (
      !matterGuid
      || !deepAnalysisEnabled
      || deepAnalysisAvailabilityLoading
      || autoSelectedRef.current
      || chronologiesLoading
    ) return;
    autoSelectedRef.current = true;
    setActiveType(chronologies[0]?.chronology_type ?? null);
  }, [matterGuid, deepAnalysisEnabled, deepAnalysisAvailabilityLoading, chronologies, chronologiesLoading]);

  // Trunk is matter-scaffolding and isn't surfaced in the tab strip — but
  // the per-matter cap is on user-facing chronologies (everything except
  // trunk) so count those.
  const userChronologyCount = useMemo(
    () => chronologies.filter(c => c.chronology_type !== 'trunk').length,
    [chronologies],
  );
  const atChronologyCap = userChronologyCount >= CHRONOLOGIES_PER_MATTER_CAP;

  // Scope the chronology configs passed to the panel to whatever the user is
  // looking at. The panel uses these to source the available filter pills
  // (tag categories/tags) so they show even when 0 events match.
  const visibleChronologies = useMemo(
    () => activeChronology ? [activeChronology] : chronologies,
    [activeChronology, chronologies],
  );

  // Use ref so the callback always sees current tabs without re-creating the DetailNavigator
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Build a flat tag-category → colour lookup from the chronology tag config.
  const colorByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const c of chronologies) {
      for (const group of c.tag_groups || []) {
        m.set(group.label, group.color);
        m.set(group.group_key, group.color);
      }
    }
    return m;
  }, [chronologies]);

  const handleDocumentClick = useCallback((documentGuid: string, focus?: CitationFocus) => {
    const docTab: SidePanelTab = {
      id: DOCUMENT_TAB_ID,
      label: 'Document',
      icon: FileText,
      content: (
        <DocumentViewerContent
          key={`${documentGuid}-${focus?.canonicalId ?? ''}`}
          documentGuid={documentGuid}
          focusCitation={focus}
        />
      ),
    };
    const currentTabs = tabsRef.current;
    const existing = currentTabs.findIndex(t => t.id === DOCUMENT_TAB_ID);
    if (existing >= 0) {
      const updated = [...currentTabs];
      updated[existing] = docTab;
      setTabs(updated);
    } else {
      setTabs([...currentTabs, docTab]);
    }
    setActiveTab(DOCUMENT_TAB_ID);
  }, [setTabs, setActiveTab]);

  // Keep timeline selection separate from whichever side-panel content is open.
  // The side panel may switch between event details, chronology history, and
  // documents; only event detail panels should be refreshed from an event id.
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);
  const [chronologyAuditPanel, setChronologyAuditPanel] = useState<{
    chronologyId: string;
    followsSelection: boolean;
  } | null>(null);
  const eventPanelIsOpen = isOpen && tabs.some(tab => tab.id === EVENT_DETAILS_TAB_ID);

  const handleEditEvent = useCallback((event: ChronologyEvent) => {
    setEditingEvent(event);
    setEventDialogOpen(true);
  }, []);

  const buildChronologyHistoryTab = useCallback((chronology: ChronologyInfo): SidePanelTab => ({
    id: EVENT_DETAILS_TAB_ID,
    label: 'Details',
    icon: Network,
    content: (
      <AuditPanel
        kind="chronology"
        matterGuid={matterGuid}
        chronologyId={chronology.id}
        chronologyName={chronology.name}
        onChanged={() => mutate()}
      />
    ),
  }), [matterGuid, mutate]);

  const openChronologyAuditPanel = useCallback((
    chronology: ChronologyInfo,
    options: { followsSelection?: boolean } = {},
  ) => {
    setFocusedEventId(null);
    setChronologyAuditPanel({
      chronologyId: chronology.id,
      followsSelection: options.followsSelection === true,
    });
    openPanel([buildChronologyHistoryTab(chronology)], EVENT_DETAILS_TAB_ID);
  }, [buildChronologyHistoryTab, openPanel]);

  useEffect(() => {
    if (!isOpen || !chronologyAuditPanel) {
      if (!isOpen && chronologyAuditPanel) setChronologyAuditPanel(null);
      return;
    }
    if (!chronologyAuditPanel.followsSelection) return;
    if (!activeChronology) {
      setChronologyAuditPanel(null);
      closePanel();
      return;
    }
    if (chronologyAuditPanel.chronologyId !== activeChronology.id) {
      openChronologyAuditPanel(activeChronology, { followsSelection: true });
    }
  }, [activeChronology, chronologyAuditPanel, closePanel, isOpen, openChronologyAuditPanel]);

  const handleDeleteEvent = useCallback(async (event: ChronologyEvent) => {
    try {
      await softDeleteEvent(event.id);
      toast.success('Event deleted');
      setFocusedEventId(null);
      setEditingEvent(null);
      setEventDialogOpen(false);
      await mutate();
      const chronologyId = resolveChronologyId(event);
      if (chronologyId) {
        const chronology = chronologies.find(item => item.id === chronologyId);
        if (chronology) {
          openChronologyAuditPanel(chronology, { followsSelection: chronology.id === activeChronology?.id });
        } else {
          closePanel();
        }
      } else {
        closePanel();
      }
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to delete event');
    }
  }, [activeChronology?.id, softDeleteEvent, mutate, resolveChronologyId, chronologies, openChronologyAuditPanel, closePanel]);

  const buildEventTabs = useCallback((
    eventId: string,
    eventList: ChronologyEventLight[] = events,
  ): SidePanelTab[] | null => {
    const event = eventList.find(e => e.id === eventId);
    if (!event) return null;
    return [
      {
        id: EVENT_DETAILS_TAB_ID,
        label: 'Details',
        icon: Network,
        content: (
          <DetailNavigator
            key={event.id}
            item={{ kind: 'event', event }}
            events={eventList}
            entities={entities}
            onDocumentClick={handleDocumentClick}
            matterGuid={matterGuid}
            resolveChronologyId={resolveChronologyId}
            onEventChanged={async () => { await mutate(); }}
            onDeleteEvent={handleDeleteEvent}
            onEditEvent={handleEditEvent}
            colorByCategory={colorByCategory}
          />
        ),
      },
    ];
  }, [events, entities, handleDocumentClick, mutate, resolveChronologyId, handleDeleteEvent, handleEditEvent, matterGuid, colorByCategory]);

  const updateEventPanelTabs = useCallback((
    eventId: string,
    eventList: ChronologyEventLight[] = events,
    activePanelTabId: string = EVENT_DETAILS_TAB_ID,
  ) => {
    const fresh = buildEventTabs(eventId, eventList);
    if (!fresh) return false;

    const preservedTabs = isOpen
      ? tabsRef.current.filter(tab =>
        tab.id !== EVENT_DETAILS_TAB_ID
        && tab.id !== ENTITY_CASE_THEORY_TAB_ID
      )
      : [];
    const nextTabs = [...fresh, ...preservedTabs];

    if (isOpen) {
      setTabs(nextTabs);
      setActiveTab(activePanelTabId);
    } else {
      openPanel(nextTabs, activePanelTabId);
    }

    return true;
  }, [buildEventTabs, events, isOpen, openPanel, setActiveTab, setTabs]);

  const openEventPanel = useCallback((
    eventId: string,
    eventList: ChronologyEventLight[] = events,
  ) => {
    setFocusedEventId(eventId);
    setChronologyAuditPanel(null);
    updateEventPanelTabs(eventId, eventList);
  }, [events, updateEventPanelTabs]);

  const handleEventSelect = useCallback((event: ChronologyEventLight | null) => {
    if (!event) {
      setFocusedEventId(null);
      closePanel();
      return;
    }
    // Event selection always opens the event-owned panel explicitly.
    openEventPanel(event.id);
  }, [closePanel, openEventPanel]);

  const handleOpenAudit = useCallback(() => {
    if (!activeChronology) return;
    openChronologyAuditPanel(activeChronology, { followsSelection: true });
  }, [activeChronology, openChronologyAuditPanel]);

  const isSystemManagedChronology = useCallback(
    (chronologyType?: string | null) => !!chronologyType && SYSTEM_CHRONOLOGY_TYPES.has(chronologyType),
    [],
  );

  const canDeleteActiveChronology = !!activeChronology
    && !isSystemManagedChronology(activeChronology.chronology_type);
  const canConfigureActiveChronology = !!activeChronology
    && activeChronology.chronology_type !== 'trunk';

  const handleDeleteChronology = useCallback(async () => {
    if (!activeChronology) return;
    if (!canDeleteActiveChronology) return;
    if (!confirm(
      `Delete the "${activeChronology.name}" chronology? `
      + 'All its events, history and tag vocabulary will be removed. '
      + 'This cannot be undone.',
    )) return;
    try {
      await deleteChronology(activeChronology.id);
      toast.success(`Deleted "${activeChronology.name}"`);
      // Fall back to "All" — the deleted chronology's tab is gone, and
      // any other selection would be arbitrary.
      setActiveType(null);
      closePanel();
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to delete chronology');
    }
  }, [activeChronology, canDeleteActiveChronology, deleteChronology, closePanel]);

  const handleNewEvent = useCallback(() => {
    if (!activeChronology || !matterGuid) return;
    setEditingEvent(null);
    setEventDialogOpen(true);
  }, [activeChronology, matterGuid]);

  const dialogChronology = useMemo(() => {
    if (dialogTarget === 'create' || dialogTarget == null) return null;
    return chronologies.find(c => c.id === dialogTarget) ?? null;
  }, [dialogTarget, chronologies]);

  // Edit mode resolves from the event's chronology; create mode falls back
  // to whichever chronology tab is active.
  const eventDialogChronologyId = editingEvent
    ? resolveChronologyId(editingEvent)
    : activeChronology?.id ?? null;

  // Rendered in the shell header (below the title) via useShellHeaderBelow, not inline.
  // Memoized so the shell header doesn't re-dispatch on every unrelated re-render.
  const chronologyTabsNav = useMemo(
    () => {
      if (deepAnalysisAvailabilityLoading || !deepAnalysisEnabled) return null;
      return (
      <div className="mt-2 flex min-h-10 items-end justify-between gap-4">
      <HeaderTabs
        items={chronologyTabs.map(({ key, label }) => ({ value: key, label }))}
        value={activeType === null ? 'all' : (activeType ?? '')}
        onChange={(value) => setActiveType(value === 'all' ? null : value)}
        idBase="chronology-view"
        trailing={customChronologiesEnabled ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    onClick={() => atChronologyCap ? null : setDialogTarget('create')}
                    disabled={atChronologyCap}
                    title={atChronologyCap ? undefined : 'New chronology'}
                    className="rounded-md px-2 py-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </span>
              </TooltipTrigger>
              {atChronologyCap && (
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  A matter can have 3 custom chronologies. Delete one to add another.
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : undefined}
      />
      {activeChronology && (
        <div className="flex shrink-0 items-center gap-1.5 pb-2">
          <Button
            variant="ghost"
            size="sm"
            icon={History}
            text="History"
            onClick={handleOpenAudit}
            className="text-slate-600"
          />
          {canConfigureActiveChronology && !canDeleteActiveChronology && (
            <Button
              variant="ghost"
              size="sm"
              icon={Settings}
              text="Configuration"
              onClick={() => setDialogTarget(activeChronology.id)}
              className="text-slate-600"
            />
          )}
          {canConfigureActiveChronology && canDeleteActiveChronology && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-600">
                <Settings2 />
                Manage
                <ChevronDown className="text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onSelect={() => setDialogTarget(activeChronology.id)} className="items-start">
                <Settings className="mt-0.5 text-slate-500" />
                <span className="flex flex-col">
                  Configuration
                  <span className="text-xs text-slate-500">Name, description, documents and tags</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={handleDeleteChronology}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
              >
                <Trash2 />
                Delete chronology
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <Button variant="outline" size="sm" icon={Plus} text="New event" onClick={handleNewEvent} />
        </div>
      )}
      </div>
      );
    },
    [
      chronologyTabs,
      activeType,
      atChronologyCap,
      customChronologiesEnabled,
      deepAnalysisAvailabilityLoading,
      deepAnalysisEnabled,
      activeChronology,
      canConfigureActiveChronology,
      canDeleteActiveChronology,
      handleOpenAudit,
      handleDeleteChronology,
      handleNewEvent,
    ],
  );

  useShellHeaderBelow(chronologyTabsNav);

  return (
    <div className="overflow-x-hidden h-full flex flex-col">
      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : deepAnalysisAvailabilityLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Loading...</div>
      ) : !deepAnalysisEnabled ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          Deep Analysis is not enabled for this matter.
        </div>
      ) : (
        <>
          <div
            role="tabpanel"
            id={`chronology-view-panel-${activeType ?? 'all'}`}
            aria-labelledby={`chronology-view-tab-${activeType ?? 'all'}`}
            tabIndex={0}
            className="flex-1 min-h-0 focus-visible:outline-hidden"
          >
            {!eventScopeReady || isLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">Loading events...</div>
            ) : isError ? (
              <div className="flex items-center justify-center py-20 text-red-400">Failed to load chronology events.</div>
            ) : (
              <ChronologyPanel
                events={events}
                onEventSelect={handleEventSelect}
                selectedEventId={focusedEventId}
                matterGuid={matterGuid}
                chronologyId={activeChronology?.id ?? null}
                description={chronologyDescription}
                refreshStatus={chronologyRefreshStatus}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                colorByCategory={colorByCategory}
                chronologies={visibleChronologies}
                framed
              />
            )}
          </div>
        </>
      )}
      {matterGuid && (
        <ChronologyDialog
          matterGuid={matterGuid}
          chronology={dialogChronology}
          open={dialogTarget != null}
          onOpenChange={(open) => { if (!open) setDialogTarget(null); }}
          onSaved={(saved) => {
            setActiveType(saved.chronology_type);
            void mutatePipelineProgress();
          }}
        />
      )}
      {matterGuid && deepAnalysisEnabled && (
        <DeepAnalysisDocumentDialog
          open={analysisDialogOpen}
          onOpenChange={setAnalysisDialogOpen}
          matterGuid={matterGuid}
          onChanged={handleAnalysisDocumentsChanged}
        />
      )}
      {matterGuid && eventDialogChronologyId && (
        <EventEditDialog
          matterGuid={matterGuid}
          chronologyId={eventDialogChronologyId}
          event={editingEvent}
          open={eventDialogOpen}
          onOpenChange={setEventDialogOpen}
          onSaved={async (opts) => {
            const refreshed = opts?.revalidate !== false ? await mutate() : undefined;
            if (opts?.focusEventId && eventPanelIsOpen) {
              setFocusedEventId(opts.focusEventId);
              updateEventPanelTabs(opts.focusEventId, refreshed?.events ?? events);
            }
          }}
        />
      )}
    </div>
  );
};

export default Chronology;
